import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useCreatePldRule, useUpdatePldRule, usePldRuleDetail } from '../../../hooks/usePld';
import { ArrowLeft, Save, AlertTriangle } from 'lucide-react';

const ruleTypes = [
  { value: 'HIGH_VALUE', label: 'Valor Alto', description: 'Transações acima de um valor limite' },
  { value: 'FREQUENCY', label: 'Frequência', description: 'Muitas transações em curto período' },
  { value: 'UNUSUAL_TIME', label: 'Horário Incomum', description: 'Transações em horários suspeitos' },
  { value: 'STRUCTURING', label: 'Fracionamento', description: 'Divisão de valores para evitar limites' },
  { value: 'ROUND_AMOUNTS', label: 'Valores Redondos', description: 'Transações com valores exatos' },
  { value: 'NEW_ACCOUNT', label: 'Conta Nova', description: 'Atividade intensa em conta recente' },
  { value: 'GEOGRAPHIC', label: 'Geográfico', description: 'Transações de locais suspeitos' },
  { value: 'CUSTOM', label: 'Personalizado', description: 'Regra com configuração customizada' },
];

const severityOptions = [
  { value: 'LOW', label: 'Baixa', color: 'bg-blue-500' },
  { value: 'MEDIUM', label: 'Média', color: 'bg-yellow-500' },
  { value: 'HIGH', label: 'Alta', color: 'bg-orange-500' },
  { value: 'CRITICAL', label: 'Crítica', color: 'bg-red-500' },
];

const defaultConfigs: Record<string, any> = {
  HIGH_VALUE: { threshold: 50000 },
  FREQUENCY: { maxTransactions: 10, periodHours: 24 },
  UNUSUAL_TIME: { startHour: 0, endHour: 6 },
  STRUCTURING: { threshold: 10000, maxTransactions: 5, periodHours: 24 },
  ROUND_AMOUNTS: { minAmount: 1000, tolerance: 0 },
  NEW_ACCOUNT: { accountAgeDays: 30, maxTransactions: 5 },
  GEOGRAPHIC: { blockedCountries: [], blockedStates: [] },
  CUSTOM: { expression: '' },
};

export default function PldRuleCreate() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEditing = !!id;
  
  const { data: existingRule, isLoading: loadingRule } = usePldRuleDetail(id || '');
  const createMutation = useCreatePldRule();
  const updateMutation = useUpdatePldRule();
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    type: 'HIGH_VALUE',
    severity: 'MEDIUM',
    isActive: true,
    config: defaultConfigs['HIGH_VALUE'],
  });

  // Load existing rule data
  useState(() => {
    if (existingRule) {
      setFormData({
        name: existingRule.name,
        description: existingRule.description || '',
        type: existingRule.type,
        severity: existingRule.severity,
        isActive: existingRule.isActive,
        config: existingRule.config || defaultConfigs[existingRule.type],
      });
    }
  });

  const handleTypeChange = (type: string) => {
    setFormData(prev => ({
      ...prev,
      type,
      config: defaultConfigs[type] || {},
    }));
  };

  const handleConfigChange = (key: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      config: { ...prev.config, [key]: value },
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      if (isEditing) {
        await updateMutation.mutateAsync({ id: id!, ...formData });
      } else {
        await createMutation.mutateAsync(formData);
      }
      navigate('/admin/compliance/pld/rules');
    } catch (err) {
      console.error('Erro ao salvar regra:', err);
    }
  };

  const renderConfigFields = () => {
    switch (formData.type) {
      case 'HIGH_VALUE':
        return (
          <div>
            <label className="block text-sm font-medium mb-2">Valor Limite (R$)</label>
            <input
              type="number"
              value={formData.config.threshold || 0}
              onChange={(e) => handleConfigChange('threshold', Number(e.target.value))}
              className="w-full px-4 py-3 bg-surface-700 border border-surface-600 rounded-lg focus:ring-2 focus:ring-primary-500"
              placeholder="50000"
            />
            <p className="text-sm text-surface-400 mt-1">Transações acima deste valor gerarão alerta</p>
          </div>
        );
        
      case 'FREQUENCY':
        return (
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">Máximo de Transações</label>
              <input
                type="number"
                value={formData.config.maxTransactions || 0}
                onChange={(e) => handleConfigChange('maxTransactions', Number(e.target.value))}
                className="w-full px-4 py-3 bg-surface-700 border border-surface-600 rounded-lg focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Período (horas)</label>
              <input
                type="number"
                value={formData.config.periodHours || 0}
                onChange={(e) => handleConfigChange('periodHours', Number(e.target.value))}
                className="w-full px-4 py-3 bg-surface-700 border border-surface-600 rounded-lg focus:ring-2 focus:ring-primary-500"
              />
            </div>
          </div>
        );
        
      case 'UNUSUAL_TIME':
        return (
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">Hora Início</label>
              <input
                type="number"
                min="0"
                max="23"
                value={formData.config.startHour || 0}
                onChange={(e) => handleConfigChange('startHour', Number(e.target.value))}
                className="w-full px-4 py-3 bg-surface-700 border border-surface-600 rounded-lg focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Hora Fim</label>
              <input
                type="number"
                min="0"
                max="23"
                value={formData.config.endHour || 0}
                onChange={(e) => handleConfigChange('endHour', Number(e.target.value))}
                className="w-full px-4 py-3 bg-surface-700 border border-surface-600 rounded-lg focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <p className="col-span-2 text-sm text-surface-400">
              Transações entre {formData.config.startHour}h e {formData.config.endHour}h gerarão alerta
            </p>
          </div>
        );
        
      case 'STRUCTURING':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">Valor Limite (R$)</label>
              <input
                type="number"
                value={formData.config.threshold || 0}
                onChange={(e) => handleConfigChange('threshold', Number(e.target.value))}
                className="w-full px-4 py-3 bg-surface-700 border border-surface-600 rounded-lg focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Máximo de Transações</label>
                <input
                  type="number"
                  value={formData.config.maxTransactions || 0}
                  onChange={(e) => handleConfigChange('maxTransactions', Number(e.target.value))}
                  className="w-full px-4 py-3 bg-surface-700 border border-surface-600 rounded-lg focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Período (horas)</label>
                <input
                  type="number"
                  value={formData.config.periodHours || 0}
                  onChange={(e) => handleConfigChange('periodHours', Number(e.target.value))}
                  className="w-full px-4 py-3 bg-surface-700 border border-surface-600 rounded-lg focus:ring-2 focus:ring-primary-500"
                />
              </div>
            </div>
          </div>
        );
        
      case 'ROUND_AMOUNTS':
        return (
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">Valor Mínimo (R$)</label>
              <input
                type="number"
                value={formData.config.minAmount || 0}
                onChange={(e) => handleConfigChange('minAmount', Number(e.target.value))}
                className="w-full px-4 py-3 bg-surface-700 border border-surface-600 rounded-lg focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Tolerância (centavos)</label>
              <input
                type="number"
                value={formData.config.tolerance || 0}
                onChange={(e) => handleConfigChange('tolerance', Number(e.target.value))}
                className="w-full px-4 py-3 bg-surface-700 border border-surface-600 rounded-lg focus:ring-2 focus:ring-primary-500"
              />
            </div>
          </div>
        );
        
      case 'NEW_ACCOUNT':
        return (
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">Idade da Conta (dias)</label>
              <input
                type="number"
                value={formData.config.accountAgeDays || 0}
                onChange={(e) => handleConfigChange('accountAgeDays', Number(e.target.value))}
                className="w-full px-4 py-3 bg-surface-700 border border-surface-600 rounded-lg focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Máximo de Transações</label>
              <input
                type="number"
                value={formData.config.maxTransactions || 0}
                onChange={(e) => handleConfigChange('maxTransactions', Number(e.target.value))}
                className="w-full px-4 py-3 bg-surface-700 border border-surface-600 rounded-lg focus:ring-2 focus:ring-primary-500"
              />
            </div>
          </div>
        );
        
      case 'CUSTOM':
        return (
          <div>
            <label className="block text-sm font-medium mb-2">Expressão Customizada</label>
            <textarea
              value={formData.config.expression || ''}
              onChange={(e) => handleConfigChange('expression', e.target.value)}
              rows={4}
              className="w-full px-4 py-3 bg-surface-700 border border-surface-600 rounded-lg focus:ring-2 focus:ring-primary-500 font-mono text-sm"
              placeholder="amount > 10000 && frequency > 5"
            />
            <p className="text-sm text-surface-400 mt-1">
              Use variáveis: amount, frequency, accountAge, hour, dayOfWeek
            </p>
          </div>
        );
        
      default:
        return null;
    }
  };

  if (isEditing && loadingRule) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-surface-700 rounded w-1/4"></div>
          <div className="h-64 bg-surface-700 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <button
          onClick={() => navigate('/admin/compliance/pld/rules')}
          className="p-2 hover:bg-surface-700 rounded-lg transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div>
          <h1 className="text-2xl font-bold">
            {isEditing ? 'Editar Regra PLD' : 'Nova Regra PLD'}
          </h1>
          <p className="text-surface-400">
            Configure uma regra de prevenção à lavagem de dinheiro
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Info */}
        <div className="bg-surface-800 rounded-xl p-6 border border-surface-700">
          <h2 className="text-lg font-semibold mb-4">Informações Básicas</h2>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">Nome da Regra</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                className="w-full px-4 py-3 bg-surface-700 border border-surface-600 rounded-lg focus:ring-2 focus:ring-primary-500"
                placeholder="Ex: Transações de Alto Valor"
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-2">Descrição</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                rows={3}
                className="w-full px-4 py-3 bg-surface-700 border border-surface-600 rounded-lg focus:ring-2 focus:ring-primary-500"
                placeholder="Descreva o objetivo desta regra..."
              />
            </div>
          </div>
        </div>

        {/* Rule Type */}
        <div className="bg-surface-800 rounded-xl p-6 border border-surface-700">
          <h2 className="text-lg font-semibold mb-4">Tipo de Regra</h2>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {ruleTypes.map((type) => (
              <button
                key={type.value}
                type="button"
                onClick={() => handleTypeChange(type.value)}
                className={`p-4 rounded-lg border text-left transition-colors ${
                  formData.type === type.value
                    ? 'border-primary-500 bg-primary-500/10'
                    : 'border-surface-600 hover:border-surface-500'
                }`}
              >
                <p className="font-medium text-sm">{type.label}</p>
                <p className="text-xs text-surface-400 mt-1">{type.description}</p>
              </button>
            ))}
          </div>
        </div>

        {/* Configuration */}
        <div className="bg-surface-800 rounded-xl p-6 border border-surface-700">
          <h2 className="text-lg font-semibold mb-4">Configuração</h2>
          {renderConfigFields()}
        </div>

        {/* Severity */}
        <div className="bg-surface-800 rounded-xl p-6 border border-surface-700">
          <h2 className="text-lg font-semibold mb-4">Severidade</h2>
          
          <div className="grid grid-cols-4 gap-3">
            {severityOptions.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, severity: option.value }))}
                className={`p-4 rounded-lg border text-center transition-colors ${
                  formData.severity === option.value
                    ? 'border-primary-500 bg-primary-500/10'
                    : 'border-surface-600 hover:border-surface-500'
                }`}
              >
                <div className={`w-3 h-3 rounded-full ${option.color} mx-auto mb-2`}></div>
                <p className="font-medium">{option.label}</p>
              </button>
            ))}
          </div>
        </div>

        {/* Status */}
        <div className="bg-surface-800 rounded-xl p-6 border border-surface-700">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold">Status</h2>
              <p className="text-surface-400 text-sm">Ativar ou desativar esta regra</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={formData.isActive}
                onChange={(e) => setFormData(prev => ({ ...prev, isActive: e.target.checked }))}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-surface-600 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-500/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
            </label>
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-4">
          <button
            type="submit"
            disabled={createMutation.isPending || updateMutation.isPending}
            className="flex items-center gap-2 px-6 py-3 bg-primary-600 hover:bg-primary-700 rounded-lg font-medium disabled:opacity-50"
          >
            <Save className="w-5 h-5" />
            {createMutation.isPending || updateMutation.isPending ? 'Salvando...' : 'Salvar Regra'}
          </button>
          <button
            type="button"
            onClick={() => navigate('/admin/compliance/pld/rules')}
            className="px-6 py-3 bg-surface-700 hover:bg-surface-600 rounded-lg font-medium"
          >
            Cancelar
          </button>
        </div>
      </form>
    </div>
  );
}
